import { useMemo } from 'react';

/**
 * Pure price calculation — mirrors PHP Pricing::calculate() exactly.
 *
 * @param {Record<string,true>} selected
 * @param {Object}              fieldConfig
 * @param {Array}               tiles
 * @returns {{ total: number, lines: Array }}
 */
export function calculatePrice( selected, fieldConfig, tiles ) {
	const empty = { total: 0, lines: [] };
	if ( ! fieldConfig || ! tiles?.length ) return empty;

	const selectedIds = Object.keys( selected ?? {} );
	if ( ! selectedIds.length ) return empty;

	const basePrice  = fieldConfig.base_price ?? 100;
	const categories = fieldConfig.categories ?? {};
	const bundles    = fieldConfig.bundles    ?? {};

	const tileCategory = {};
	for ( const tile of tiles ) tileCategory[ tile.id ] = tile.category;

	const bundleTileMap = {};
	for ( const tile of tiles ) {
		if ( tile.bundleId ) {
			if ( ! bundleTileMap[ tile.bundleId ] ) bundleTileMap[ tile.bundleId ] = [];
			bundleTileMap[ tile.bundleId ].push( tile.id );
		}
	}

	let total       = 0;
	const lines     = [];
	const accounted = new Set();

	// ── Bundle lines ──────────────────────────────────────────────────────
	for ( const [ bundleId, bundle ] of Object.entries( bundles ) ) {
		const bundleTileIds = bundleTileMap[ bundleId ] ?? [];
		if ( ! bundleTileIds.length ) continue;
		if ( ! bundleTileIds.every( ( id ) => !! selected[ id ] ) ) continue;

		const slug        = tileCategory[ bundleTileIds[ 0 ] ] ?? 'default';
		const factor      = categories[ slug ]?.factor ?? 1.0;
		const priceEach   = basePrice * factor;
		const bundlePrice = bundleTileIds.length * priceEach;

		total += bundlePrice;
		lines.push( { type: 'bundle', label: bundle.label, count: 1, priceEach: bundlePrice, subtotal: bundlePrice } );
		bundleTileIds.forEach( ( id ) => accounted.add( id ) );
	}

	// ── Category lines ────────────────────────────────────────────────────
	const catCounts = {};
	for ( const tileId of selectedIds ) {
		if ( accounted.has( tileId ) ) continue;
		const slug = tileCategory[ tileId ] ?? 'default';
		catCounts[ slug ] = ( catCounts[ slug ] ?? 0 ) + 1;
	}

	for ( const [ slug, count ] of Object.entries( catCounts ) ) {
		const factor    = categories[ slug ]?.factor ?? 1.0;
		const priceEach = basePrice * factor;
		const subtotal  = priceEach * count;
		const label     = categories[ slug ]?.label ?? slug;
		total += subtotal;
		lines.push( { type: 'category', label, count, priceEach, subtotal } );
	}

	return { total: Math.round( total * 100 ) / 100, lines };
}

/**
 * React hook wrapping calculatePrice() with useMemo.
 */
export function usePrice( selected, fieldConfig, tiles ) {
	return useMemo(
		() => calculatePrice( selected, fieldConfig, tiles ),
		[ selected, fieldConfig, tiles ]
	);
}

export function formatPrice( price ) {
	return price.toLocaleString( 'de-DE', { style: 'currency', currency: 'EUR' } );
}
